import {
  getPendingChunks,
  markChunkSent,
} from '@/services/audioChunkStore';
import { recordingSessionApi } from '@/services/recordingSessionApi';

/**
 * REST drain path for IDB chunks that the live socket either failed to
 * deliver or was never available for in the first place. Walks every
 * `pending` chunk for the session in chunkIndex order, posts each to
 * `PUT /recording-sessions/:id/audio-chunks/:chunkIndex`, and flips
 * the IDB row to `sent` on a successful response.
 *
 * Idempotent at every level:
 *   - getPendingChunks returns only `pending` rows, so a re-run picks
 *     up where a prior run stopped.
 *   - The backend upsert on `(sessionId, chunkIndex)` makes a duplicate
 *     PUT a no-op.
 *   - markChunkSent is also idempotent.
 *
 * Errors are surfaced via the returned summary, not thrown — the
 * drain loop continues past a single failed chunk so a transient
 * network blip on chunk 12 doesn't block chunks 13-60. The caller
 * decides whether to retry the failed chunks or surface to the user.
 */
export interface DrainSummary {
  uploaded: number;
  failed: number;
  total: number;
  firstError?: Error;
}

export const drainIdbToRest = async (
  sessionId: string,
): Promise<DrainSummary> => {
  const pending = await getPendingChunks(sessionId);
  const summary: DrainSummary = {
    uploaded: 0,
    failed: 0,
    total: pending.length,
  };
  if (pending.length === 0) return summary;

  for (const row of pending) {
    try {
      await recordingSessionApi.uploadChunk(
        sessionId,
        row.chunkIndex,
        row.blob,
      );
      await markChunkSent(sessionId, row.chunkIndex);
      summary.uploaded += 1;
    } catch (err) {
      summary.failed += 1;
      if (!summary.firstError) {
        summary.firstError =
          err instanceof Error ? err : new Error(String(err));
      }
      // Bail on the first auth/perm error — repeating 401/403 against
      // the same endpoint just spams the server. For 5xx and network
      // blips, we keep going so a partial drain still makes progress.
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status === 401 || status === 403) break;
    }
  }
  return summary;
};
